import {
    Card,
    CardContent,
    CardMedia,
    Container,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from "@mui/material";
import Box from "@mui/material/Box";
import React, {useEffect, useState} from "react";
import {fetchBureau} from "../api/bureauService";


export default function LeClub() {
    // initialiser bureau avec une forme sûre pour éviter les erreurs de lecture
    const [bureau, setBureau] = useState({ presentation: '', membres: [] });
    const [loadingBureau, setLoadingBureau] = useState(true);
    const [BureauError, setBureauError] = useState(null);

    // modal state
    const [selectedMember, setSelectedMember] = useState(null);
    const handleOpen = (membre) => setSelectedMember(membre);
    const handleClose = () => setSelectedMember(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoadingBureau(true);
            try {
                const loaded = await fetchBureau();
                // loaded devrait être un objet { presentation, membres }
                if (mounted) setBureau(loaded || { presentation: '', membres: [] });
            } catch (err) {
                if (mounted) setBureauError(err.message || "Erreur de chargement");
            } finally {
                if (mounted) setLoadingBureau(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    if (loadingBureau) return <Typography>Chargement des informations du Club…</Typography>;
    if (BureauError) return <Typography color="error">Erreur: {BureauError}</Typography>;


    return (

        <Box sx={{paddingBottom: '50px', paddingTop: '5px'}}>
            <Container>
                <Typography variant="h4" align="center" gutterBottom sx={{fontWeight: 700}}>
                    Le Club
                </Typography>

                {/* Bloc de présentation centré, largeur max, fond léger, accent visuel */}
                <Box sx={{
                    backgroundColor: '#fbfdff', // léger fond bleu très pâle
                    p: { xs: 2, md: 4 },
                    borderRadius: 3,
                    boxShadow: 2,
                    // width: '80%',
                    margin: '0 auto 28px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    textAlign: { xs: 'left', md: 'justify' }
                }}>
                    {/* Accent coloré à gauche + petit titre */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <Box sx={{width: 8, height: 40, backgroundColor: '#df0707cf', borderRadius: 1}} />
                        <Typography variant="h6" sx={{fontWeight: 700, letterSpacing: 0.2}}>Présentation</Typography>
                    </Box>

                    <Typography variant="body1" sx={{whiteSpace: 'pre-line', lineHeight: 1.8, color: 'text.primary', fontSize: { xs: '0.95rem', md: '1rem' }, width: '100%' }}>
                        {bureau.presentation}
                    </Typography>
                </Box>
             </Container>


             <Box sx={{
                 flexDirection: "column",
                 gap: 3,
                 paddingTop: '20px',
                 maxWidth: '1200px',
                 margin: "auto",
                 backgroundColor: "#ffffff"
             }}>

                {/* Grille CSS responsive : 1 colonne sur xs, 2 sur sm, 3 sur md+ */}
                <Box sx={{
                    display: 'grid',
                    // espacement vertical plus important (rowGap) tout en gardant l'espacement horizontal
                    rowGap: 8, // équivalent à theme.spacing(4)
                    columnGap: 3, // équivalent à theme.spacing(3)
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)'
                    }
                }}>
                    {(bureau.membres || []).map((membre, index) => {
                        // Support both formats: `image` (single object) or `images` (array)
                        const imageObj = membre.image || (membre.images && membre.images.length ? membre.images[0] : null);
                        // Normaliser les chemins d'images fournis dans public/bureau/bureau.json
                        const normalizeUrl = (url) => {
                            if (!url) return null;
                            // déja absolu ou externe
                            if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) return url;
                            // ../images/bureau/Marie.png -> /images/bureau/Marie.png
                            if (url.startsWith('../')) return '/' + url.replace(/^\.\.\//, '');
                            // ./images/... -> /images/...
                            if (url.startsWith('./')) return '/' + url.replace(/^\.\//, '');
                            // tout autre chemin relatif -> prepend /
                            return '/' + url.replace(/^\/?/, '');
                        };

                        const imageUrl = imageObj && imageObj.url ? normalizeUrl(imageObj.url) : null;

                        return (
                            <Box key={membre.id || index}>
                                <Card
                                    onClick={() => handleOpen(membre)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        padding: 2,
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'transform 150ms ease, box-shadow 150ms ease',
                                        boxShadow: 1,
                                        '&:hover': {
                                            boxShadow: 6,
                                            transform: 'translateY(-4px)'
                                        }
                                    }}
                                >
                                    {imageUrl && (
                                        <CardMedia
                                            component="img"
                                            alt={imageObj.title || `${membre.prenom} ${membre.nom}`}
                                            image={imageUrl}
                                            sx={{
                                                width: 'auto',
                                                height: 'auto',
                                                // maxWidth: 160,
                                                maxHeight: 160,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                mb: 2,
                                                maxWidth: '100%'
                                            }}
                                        />
                                    )}

                                    <CardContent sx={{
                                        textAlign: 'center',
                                        padding: 0,
                                        '&:last-child': { paddingBottom: 0 }
                                    }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {membre.role}
                                        </Typography>
                                        <Typography variant="h6">
                                            {membre.prenom} {membre.nom}
                                        </Typography>
                                    </CardContent>

                                </Card>
                            </Box>
                        );
                    })}
                </Box>

            </Box>

            {/* Modal pour afficher la présentation du membre */}
            <Dialog
                open={Boolean(selectedMember)}
                onClose={handleClose}
                aria-labelledby="member-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="member-dialog-title">{selectedMember ? `${selectedMember.prenom} ${selectedMember.nom}` : ''}</DialogTitle>
                <DialogContent dividers>
                    <DialogContentText component="div">
                        <Typography sx={{whiteSpace: 'pre-line'}}>
                            {selectedMember ? selectedMember.text : ''}
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="contained">Fermer</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}
